import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Router from 'next/router';

import { MainApiService } from '_services';
import { STORAGE_KEYS } from '_constants/storage';
import { processResponse, processError } from '_helpers/api';

import {
  storeSessionValue,
  restoreSessionValue,
  clearSessionValue,
  restoreLocalValue,
} from '_helpers/storage';

import {
  createUpsellBody,
  fillUpsellBodyWithSourceData,
} from '_helpers/upsell';
import { PRODUCT_CODES } from '_constants/products';

const initForm = {
  step: 0,
  maxStep: 0,
  isOfficeAction: false,
  isKeepAlive: false,
  isCertifiedCopy: false,
  privacyPackage: 'NONE',
  trademarkAssessment: false,
  // isOfficeAction: true,
  // isKeepAlive: true,
  // isCertifiedCopy: true,
  // privacyPackage: PRODUCT_CODES.tmPrivateContactInfo,
  // trademarkAssessment: true,
};

export const createUpsell = createAsyncThunk(
  'upsell/createUpsell',
  async function (params, { dispatch, getState, rejectWithValue }) {
    const { redirectUrl } = params || {};
    const crmSessionId = restoreLocalValue(STORAGE_KEYS.crmSessionId);
    const { payment, upsell } = getState();
    const { data: orderAfterPayment } = payment;
    const { form } = upsell;

    const body = createUpsellBody(form, orderAfterPayment);

    if (!body) {
      return;
    }

    fillUpsellBodyWithSourceData(body);

    try {
      const response = await MainApiService.createUpsell({
        crmSessionId,
        orderId: orderAfterPayment?.order?.id,
        body,
      });

      const result = processResponse(response);

      dispatch(storeOrderAfterUpsell(result));
      dispatch(resetForm());
      clearSessionValue(STORAGE_KEYS.upsellInfo);

      if (redirectUrl) {
        Router.push(redirectUrl);
      }

      return result;
    } catch (e) {
      return rejectWithValue(processError(e));
    }
  }
);

const upsellSlice = createSlice({
  name: 'upsell',
  initialState: {
    form: {
      ...initForm,
    },
    data: null,
    error: null,
    loading: false,
    restored: false,
  },
  reducers: {
    refreshForm(state, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedUpsellInfo =
          restoreSessionValue(STORAGE_KEYS.upsellInfo) || {};

        storeSessionValue(STORAGE_KEYS.upsellInfo, {
          ...storedUpsellInfo,
          [trademarkServiceKey]: {
            ...state.form,
            ...action.payload,
          },
        });
      }

      state.form = {
        ...state.form,
        ...action.payload,
      };
    },
    restoreForm(state, action) {
      if (action.payload) {
        const storedUpsellInfo =
          restoreSessionValue(STORAGE_KEYS.upsellInfo) || {};
        const storedForm = storedUpsellInfo[action.payload];

        if (storedForm) {
          state.form = {
            ...state.form,
            ...storedForm,
            restored: true,
          };
        } else {
          state.form = { ...initForm, restored: true };
        }
      }
    },
    resetForm(state) {
      state.form = { ...initForm };
    },
    storeOrderAfterUpsell(_, action) {
      const trademarkServiceKey = restoreSessionValue(
        STORAGE_KEYS.trademarkServiceKey
      );

      if (trademarkServiceKey) {
        const storedOrderAfterUpsell =
          restoreSessionValue(STORAGE_KEYS.orderAfterUpsell) || {};

        storeSessionValue(STORAGE_KEYS.orderAfterUpsell, {
          ...storedOrderAfterUpsell,
          [trademarkServiceKey]: {
            ...(action.payload || {}),
          },
        });
      }
    },
    restoreOrderAfterUpsell(state, action) {
      if (action.payload) {
        const storedOrderAfterUpsell =
          restoreSessionValue(STORAGE_KEYS.orderAfterUpsell) || {};
        const storedOrderAfterUpsellByServiceKey =
          storedOrderAfterUpsell[action.payload] || null;
        state.data = storedOrderAfterUpsellByServiceKey;
        state.restored = true;
      }
    },
    resetResult(state) {
      state.data = null;
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createUpsell.pending, (state) => {
        state.data = null;
        state.error = null;
        state.loading = true;
        state.restored = false;
      })
      .addCase(createUpsell.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(createUpsell.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });
  },
});

export const {
  refreshForm,
  restoreForm,
  resetForm,
  storeOrderAfterUpsell,
  restoreOrderAfterUpsell,
  resetResult,
  resetError,
} = upsellSlice.actions;

export default upsellSlice.reducer;
